/**
 * Durable operation key for the reviewed merge (SPEC-invoice-merge-history-2026-10-02 R4 AC6).
 *
 * A merge that was SENT must be recoverable after a lost response, a reload or from a second tab:
 * every one of those reuses the SAME operation key and request, and the server (which applies
 * a key at most once and answers a repeat with the original committed result) is the only authority on
 * whether it happened. This module never decides an outcome; it only remembers what was sent.
 *
 *   * `pending`   a request left this browser and its outcome is not known to it.
 *   * `committed` the server confirmed it; the result is kept so a second tab or a reload shows it.
 *
 * Records live in localStorage (shared by tabs, survives reload), keyed by the PAIR of invoice ids
 * (order-independent), because an invoice can only be absorbed once: one outstanding operation per
 * pair is the whole model. Tenant is never stored or sent -- it comes from authentication.
 *
 * A changed plan forces re-review: `runMergeApply` never sends new content under an old key. If the
 * caller's request differs from a pending one, the pending one is RESOLVED first (replayed
 * unchanged); only a definitive refusal clears it, and even then the caller is told to review
 * again rather than silently submitting the new content.
 *
 * Free of react and api imports (the sender is injected) so it is unit-testable alone.
 */
import type { MergeApplyRequest, MergeApplyResult } from './invoice-merge-plan';

const KEY_PREFIX = 'invoice-merge-operation:v1:';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export interface StoredMergeOperation {
  operationKey: string;
  request: MergeApplyRequest;
  state: 'pending' | 'committed';
  result?: MergeApplyResult;
  createdAt: number;
}

/** The slice of the Storage interface this module needs (localStorage, or an in-memory stand-in). */
export interface MergeOperationStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const memory = new Map<string, string>();
const memoryStorage: MergeOperationStorage = {
  getItem: (k) => memory.get(k) ?? null,
  setItem: (k, v) => void memory.set(k, v),
  removeItem: (k) => void memory.delete(k),
};

function defaultStorage(): MergeOperationStorage {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const probe = `${KEY_PREFIX}probe`;
      window.localStorage.setItem(probe, '1');
      window.localStorage.removeItem(probe);
      return window.localStorage;
    }
  } catch {
    // Storage may be unavailable (private browsing, quota). Fall back to memory: double-submit and
    // same-tab recovery still work; reload and second-tab recovery do not, and the UI says so.
  }
  return memoryStorage;
}

export function pairKey(idA: string, idB: string): string {
  return `${KEY_PREFIX}${[idA, idB].sort().join(':')}`;
}

export function newOperationKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `op-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/** True when durable (cross-tab, reload-surviving) storage is available. */
export function hasDurableMergeStorage(storage: MergeOperationStorage = defaultStorage()): boolean {
  return storage !== memoryStorage;
}

export function loadMergeOperation(
  idA: string,
  idB: string,
  storage: MergeOperationStorage = defaultStorage(),
  now: number = Date.now(),
): StoredMergeOperation | null {
  const key = pairKey(idA, idB);
  const raw = storage.getItem(key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredMergeOperation;
    if (
      !parsed ||
      typeof parsed.operationKey !== 'string' ||
      !parsed.request ||
      (parsed.state !== 'pending' && parsed.state !== 'committed') ||
      now - parsed.createdAt > MAX_AGE_MS
    ) {
      // An old COMMITTED record is harmless to drop (the server still has the operation). An old
      // PENDING one is dropped too: after a day nobody is waiting on that response.
      storage.removeItem(key);
      return null;
    }
    return parsed;
  } catch {
    storage.removeItem(key);
    return null;
  }
}

function save(record: StoredMergeOperation, storage: MergeOperationStorage): void {
  const { invoice_id_a: a, invoice_id_b: b } = record.request;
  try {
    storage.setItem(pairKey(a, b), JSON.stringify(record));
  } catch {
    // Quota / unavailable: the in-flight guard still protects this tab.
  }
}

export function clearMergeOperation(idA: string, idB: string, storage: MergeOperationStorage = defaultStorage()): void {
  storage.removeItem(pairKey(idA, idB));
}

/**
 * Two requests are the SAME intent when everything the server compares is equal. The operation key
 * is excluded (it is what we are deciding to reuse). Acknowledgements are order-independent.
 */
export function sameMergeIntent(a: MergeApplyRequest, b: MergeApplyRequest): boolean {
  const norm = (r: MergeApplyRequest) =>
    JSON.stringify({
      ids: [r.invoice_id_a, r.invoice_id_b].sort(),
      choices: sortKeys(r.choices ?? {}),
      acknowledged: [...(r.acknowledged ?? [])].sort(),
      future: r.future_defaults ? { p: r.future_defaults.project_id, f: [...r.future_defaults.fields].sort() } : null,
      digest: r.plan_digest,
      version: r.plan_version,
      sources: sortKeys(r.source_versions ?? {}),
    });
  return norm(a) === norm(b);
}

function sortKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortKeys);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .sort(([x], [y]) => x.localeCompare(y))
        .map(([k, v]) => [k, sortKeys(v)]),
    );
  }
  return value;
}

export type MergeApplyOutcome =
  /** The server committed this merge (a first apply, or the original result replayed). */
  | { kind: 'committed'; result: MergeApplyResult; recovered: boolean }
  /**
   * An earlier pending request was resolved as refused. Nothing committed; the caller's NEW request was
   * NOT sent. The person must review again.
   */
  | { kind: 'review_again'; reason: unknown };

export interface MergeApplySender {
  (request: MergeApplyRequest): Promise<MergeApplyResult>;
}

const inFlight = new Map<string, Promise<MergeApplyOutcome>>();

/** An error the server answered with a 4xx status: refused, nothing committed under that key. */
function isDefinitiveRefusal(error: unknown): boolean {
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status === 'number' && status >= 400 && status < 500;
}

/**
 * Send (or recover) the reviewed merge for this pair.
 *
 *  1. A committed record answers immediately with its stored result (second tab / reload).
 *  2. A pending record is replayed byte-for-byte first (same key, same request). The server answers
 *     with the original result if it committed, or with a refusal if it did not; a refusal clears the
 *     record and asks for a fresh review, WITHOUT sending the caller's new content.
 *  3. Otherwise a new key is stored as pending, the request is sent, and the outcome recorded:
 *     success -> committed; a 4xx -> cleared; anything else (5xx, lost connection) -> stays pending
 *     and the error is rethrown so the UI can say the outcome is unconfirmed.
 *
 * Concurrent calls for the same pair in one tab share one promise (double-submit).
 */
export function runMergeApply(
  request: Omit<MergeApplyRequest, 'operation_key'>,
  send: MergeApplySender,
  storage: MergeOperationStorage = defaultStorage(),
): Promise<MergeApplyOutcome> {
  const key = pairKey(request.invoice_id_a, request.invoice_id_b);
  const existingFlight = inFlight.get(key);
  if (existingFlight) return existingFlight;

  const flight = (async (): Promise<MergeApplyOutcome> => {
    const stored = loadMergeOperation(request.invoice_id_a, request.invoice_id_b, storage);

    if (stored?.state === 'committed' && stored.result) {
      return { kind: 'committed', result: stored.result, recovered: true };
    }

    if (stored?.state === 'pending') {
      try {
        const result = await send(stored.request);
        save({ ...stored, state: 'committed', result }, storage);
        // The server may have answered with the original result (replayed) or applied it now:
        // either way this is the merge the person asked for under that key.
        return { kind: 'committed', result, recovered: true };
      } catch (error) {
        if (isDefinitiveRefusal(error)) {
          clearMergeOperation(request.invoice_id_a, request.invoice_id_b, storage);
          if (sameMergeIntent(stored.request, { ...request, operation_key: stored.operationKey })) {
            // The same intent was refused (e.g. the plan went stale): surface the refusal itself.
            throw error;
          }
          return { kind: 'review_again', reason: error };
        }
        throw error; // still unknown: keep the record, say so
      }
    }

    const full: MergeApplyRequest = { ...request, operation_key: newOperationKey() };
    save({ operationKey: full.operation_key, request: full, state: 'pending', createdAt: Date.now() }, storage);
    try {
      const result = await send(full);
      save({ operationKey: full.operation_key, request: full, state: 'committed', result, createdAt: Date.now() }, storage);
      return { kind: 'committed', result, recovered: false };
    } catch (error) {
      if (isDefinitiveRefusal(error)) clearMergeOperation(request.invoice_id_a, request.invoice_id_b, storage);
      throw error;
    }
  })();

  inFlight.set(key, flight);
  const release = () => {
    if (inFlight.get(key) === flight) inFlight.delete(key);
  };
  flight.then(release, release);
  return flight;
}

/**
 * Resolve a pending record without a new request: used when the panel opens for a pair that has an
 * outstanding operation (reload, second tab). Returns null when there is nothing to recover.
 */
export async function recoverMergeOperation(
  idA: string,
  idB: string,
  send: MergeApplySender,
  storage: MergeOperationStorage = defaultStorage(),
): Promise<MergeApplyOutcome | null> {
  const stored = loadMergeOperation(idA, idB, storage);
  if (!stored) return null;
  if (stored.state === 'committed' && stored.result) {
    return { kind: 'committed', result: stored.result, recovered: true };
  }
  const key = pairKey(idA, idB);
  const existingFlight = inFlight.get(key);
  if (existingFlight) return existingFlight;
  const flight = (async (): Promise<MergeApplyOutcome> => {
    try {
      const result = await send(stored.request);
      save({ ...stored, state: 'committed', result }, storage);
      return { kind: 'committed', result, recovered: true };
    } catch (error) {
      if (isDefinitiveRefusal(error)) {
        clearMergeOperation(idA, idB, storage);
        return { kind: 'review_again', reason: error };
      }
      throw error;
    }
  })();
  inFlight.set(key, flight);
  const release = () => {
    if (inFlight.get(key) === flight) inFlight.delete(key);
  };
  flight.then(release, release);
  return flight;
}

/** Forget a committed record once the person has seen the result and dismissed it. */
export function dismissCommittedMergeOperation(
  idA: string,
  idB: string,
  storage: MergeOperationStorage = defaultStorage(),
): void {
  const stored = loadMergeOperation(idA, idB, storage);
  if (stored?.state === 'committed') clearMergeOperation(idA, idB, storage);
}
